/****************************************************
 * Module Name:  stockTally.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import Select from "react-select";
import { Box, CircularProgress, TextField, Tooltip, Typography, Checkbox } from "@mui/material";
import Constants from "../../utility/constants";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Utility from "../../utility/utility";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { LoadingButton } from "@mui/lab";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import StockTallyProxy from "../../utility/proxy/manager/stockTallyProxy";
import dayjs from "dayjs";
import PageTitle from "../common/pageTitle";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import renderCellExpand from "../common/dataGridCellExpand";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.tableLayouts = {
    Mylayout: {
        fillColor: function (rowIndex, node, columnIndex) {
            return rowIndex % 2 === 0 ? "#CCCCCC" : null;
        },
    },
};

const StockTally = ({ isModal = false }) => {
    const [location, setLocation] = useState({});
    const [locationOptions, setLocationOptions] = useState([]);
    const [listData, setListData] = useState([]);
    const [searchLoader, setSearchLoader] = useState(false);
    const [fetchData, setFetchData] = useState(false);
    const [access, setAccess] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [locationLoader, setLocationLoader] = useState(false);
    const [toDate, setToDate] = useState(new Date());
    const [filterValues, setFilterValues] = useState({});
    const [isShowDisabledChecked, setIsShowDisabledChecked] = useState(false);

    useEffect(() => {
        PageTitle("REPORT_STOCKTALLY");
        if (!isModal) checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.REPORT_STOCKTALLY
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            prepareData();
        }
    }

    const prepareData = () => {
        StockTallyProxy.GetConfiguredBoxLocations((response) => {
            setAllLocationData(response);
        })
    }

    const setAllLocationData = (response) => {
        let optionsArray = [];
        optionsArray.push({ value: "*", label: "-- ANY --" });
        let tempOption = {};
        response.map((data) => {
            let option = { value: data.id, label: data.name };
            if (data.id === Constants.loggedInUser.locationid) {
                tempOption = option;
            }
            optionsArray.push(option);
            return true;
        });

        setLocationOptions(optionsArray);
        setLocation(tempOption.length === 1 ? tempOption : optionsArray[0]);
        setFetchData(true);
    };

    useEffect(() => {
        if (fetchData) {
            if (location && toDate) {
                handleSearch();
            }
        }
    }, [fetchData, location, toDate]);

    useEffect(() => {
        if (isModal) {
            if (toDate) {
                handleSearch();
            }
        }
    }, [toDate]);

    const handleSearch = () => {

        setSearchLoader(true);
        let locationid = [];
        if (!isModal && location.value === "*") {
            locationid = locationOptions.filter(option => option.value !== "*").map(option => option.value);
        }
        let data = {
            date: Utility.ConvertLocalTimeToUtc(toDate).slice(0, 11) + "23:59:59.999Z",
            locationid: isModal ? [Constants.loggedInUser.locationid] : locationid.length > 0 ? locationid : [location.value]
        }
        StockTallyProxy.GetStockTally(data, isShowDisabledChecked, (response) => {
            if (response) {
                let updatedResponse = response
                    .sort((a, b) => {
                        if (a.locationid !== b.locationid) {
                            return a.locationid - b.locationid;
                        }
                        return a.boxid - b.boxid; // in case of same locationid
                    })
                    .map((iData, index) => ({
                        ...iData,
                        rowid: index + 1,
                        user: (iData.userid === null || iData.username === null)
                            ? " - "
                            : `${iData.userid} | ${iData.username}`
                    }));

                if (isModal) {
                    let talliedBoxesList = updatedResponse.filter(i => (i.createdat && Utility.ConvertUtcToLocalDate(i.createdat) === Utility.ConvertUtcToLocalDate(toDate)))
                    let untalliedBoxesList = updatedResponse.filter(i => (!i.createdat || Utility.ConvertUtcToLocalDate(i.createdat) !== Utility.ConvertUtcToLocalDate(toDate)))
                    updatedResponse = untalliedBoxesList.concat(talliedBoxesList);
                }

                const uniqueFilterValues = {
                    username: Utility.getDistinctOptions(response, "username"),
                    location: Utility.getDistinctOptions(response, "locationname"),
                    boxname: Utility.getDistinctOptions(response, "boxname"),
                }

                const combinedFilterValues = {
                    user: uniqueFilterValues.username.map(value => {
                        const matchingItem = response.find(item => item.username === value);
                        return matchingItem && matchingItem.userid ? `${matchingItem.userid} | ${value}` : " - ";
                    }),
                    location: uniqueFilterValues.location,
                    boxname: uniqueFilterValues.boxname,
                }
                setFilterValues(combinedFilterValues);
                setListData(updatedResponse);
                setSearchLoader(false);
                setFetchData(false);
            } else {
                setSearchLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    const initialColumns = () => [
        {
            headerName: "ROW ID",
            field: "rowid",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80,
        },
        {
            headerName: "ID",
            field: "id",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80,
        },
        {
            headerName: "BOX ID",
            field: "boxid",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80,
        },
        {
            headerName: "BOX NAME",
            field: "boxname",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.boxname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 120,
        },
        {
            headerName: "USER",
            field: "user",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.user : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            headerName: "CREATED AT",
            field: "createdat",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => params.value ? Utility.ConvertUtcToLocalDate(params.value) : " - ",
            cellClassName: (params) => {
                return (!params.value || Utility.ConvertUtcToLocalDate(params.value) !== Utility.ConvertUtcToLocalDate(toDate)) ? 'highlight-cell' : '';
            }
        },
        {
            headerName: "LOCATION",
            field: "locationname",
            headerAlign: "center",
            align: "center",
            width: 140,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value : " - ",
        },
        {
            headerName: "TOTAL STONES",
            field: "totalstone",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            renderCell: (params) => {
                const value = params.value === null ? " - " : params.value
                return <span>{value}</span>;
            }
        },
        {
            headerName: "STONES FOUND",
            field: "nostonefound",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            renderCell: (params) => {
                const value = params.row.nostonefound === null ? " - " : params.row.nostonefound
                const style = (params.row.nostonefound !== null && params.row.nostonefound !== params.row.totalstone)
                    ? { color: 'red' } : {};
                return <span style={style}>{value}</span>;
            }
        },
        {
            headerName: "STONES NOT FOUND",
            field: "stonesnotfound",
            headerAlign: "center",
            align: "center",
            width: 600,
            renderCell: (params) => {
                const value = params.row.stonesnotfound === null || params.row.stonesnotfound === "" ? " - " : params.row.stonesnotfound
                const style = (params.row.stonesnotfound === null || params.row.stonesnotfound === "") ? {} : { color: 'red' };
                return (
                    <Tooltip style={style} title={params.row.stonesnotfound}>
                        <span>{value}</span>
                    </Tooltip>
                );
            }
        },
        {
            headerName: "No. EXTRA TAGS",
            field: "notagnotfound",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            renderCell: (params) => {
                const value = params.row.notagnotfound === null ? " - " : params.row.notagnotfound
                const style = (params.row.notagnotfound !== null && params.row.notagnotfound > 0) ? { color: 'red' } : {};
                return <span style={style}>{value}</span>;
            }
        },
        {
            headerName: "EXTRA TAGS",
            field: "tagsnotfound",
            headerAlign: "center",
            align: "center",
            width: 350,
            renderCell: (params) => {
                const value = params.row.tagsnotfound === null ? " - " : params.row.tagsnotfound
                const style = (params.row.tagsnotfound !== null) ? { color: 'red' } : {};
                return (
                    <Tooltip style={style} title={params.row.tagsnotfound}>
                        <span>{value}</span>
                    </Tooltip>

                );
            }
        },
    ]

    const columns = initialColumns();

    const CustomToolbar = ({ isShowDisabledChecked, setIsShowDisabledChecked }) => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                {!isModal && (
                    <>
                        <Checkbox
                            checked={isShowDisabledChecked}
                            onChange={(e) => {
                                setIsShowDisabledChecked(e.target.checked);
                                setFetchData(true);
                            }}
                            size="small"
                        />
                        <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>Show Disabled Box</Typography>
                    </>
                )}

                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                {searchLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => handleSearch()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}

                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    };
    return (
        <div className="background">
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <CommonSnackbar
                            show={errorSnackbar}
                            message={Constants.APiFailErrorMessage}
                            variant={"error"}
                            autoHide={Constants.SnackBarCloseTimer}
                            title={"Failed"}
                            handleClose={() => setErrorSnackbar(false)}
                        />
                        {!isModal ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "16px",
                                    margin: "18px",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: "18px",
                                        alignItems: "center",
                                        minWidth: "300px",
                                    }}
                                >
                                    <Typography>Location</Typography>
                                    <Box sx={{ minWidth: "220px", maxWidth: "276px", flex: 1 }}>
                                        <Select
                                            menuPlacement="auto"
                                            blurInputOnSelect={false}
                                            className="basic-single"
                                            classNamePrefix="select"
                                            value={location}
                                            isSearchable={true}
                                            name="Location"
                                            menuPortalTarget={document.body}
                                            styles={Constants.Styles}
                                            options={locationOptions}
                                            onChange={(selectedOption) => setLocation(selectedOption)}
                                        />
                                    </Box>
                                    {locationLoader ? (
                                        <CircularProgress size="1rem" />
                                    ) : (
                                        <Tooltip title="Reload List">
                                            <RefreshOutlinedIcon
                                                onClick={() => {
                                                    setLocationLoader(true);
                                                    prepareData();
                                                    setLocationLoader(false);
                                                }}
                                                color="primary"
                                                fontSize="medium"
                                                sx={{ cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        minWidth: "220px",
                                    }}
                                >
                                    <Typography>Date</Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            className="picker"
                                            ampm={true}
                                            renderInput={(props) => (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    sx={{ minWidth: "140px", maxWidth: "200px" }}
                                                    {...props}
                                                    inputProps={{ ...props.inputProps, readOnly: true }}
                                                />
                                            )}
                                            error={false}
                                            value={toDate}
                                            inputFormat="DD/MM/YYYY"
                                            onChange={(newValue) => {
                                                const now = dayjs();
                                                const toDate = dayjs(newValue)
                                                    .hour(now.hour())
                                                    .minute(now.minute())
                                                    .second(now.second())
                                                    .millisecond(now.millisecond())
                                                    .toDate();
                                                setToDate(toDate);
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                <Box sx={{ minWidth: "150px", display: "flex", justifyContent: "flex-start" }}>
                                    <LoadingButton
                                        variant="contained"
                                        onClick={handleSearch}
                                        startIcon={<SearchOutlinedIcon size="large" />}
                                        loading={searchLoader}
                                        loadingPosition="start"
                                        disabled={locationOptions.length === 0}
                                        sx={{ width: "100%", maxWidth: "200px" }}
                                    >
                                        Search
                                    </LoadingButton>
                                </Box>
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                px: 1,
                                width: "100%",
                                position: "relative",
                                backgroundColor: '#fff',
                                color: '#000',
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .lavenderCell": {
                                    color: "#080808 !important",
                                    backgroundColor: "#D8BFD8 !important",
                                },
                                "& .greenCell": {
                                    color: "#080808",
                                    backgroundColor: "#C6DCBA",
                                },
                                "& .brownCell": {
                                    color: "#080808",
                                    backgroundColor: "#ebbdaa",
                                },
                                "& .highlight-cell": {
                                    color: "#080808",
                                    backgroundColor: "#f5d7d7",
                                },
                                height: "74vh",
                            }}
                        >
                            <DataGridPro
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "6px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                disableRowSelectionOnClick={true}
                                slots={{
                                    toolbar: CustomToolbar,
                                }}
                                rows={listData}
                                columns={columns}
                                getRowId={(row) => row.rowid}
                                loading={searchLoader}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            rowid: false,
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        isShowDisabledChecked, setIsShowDisabledChecked
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}
        </div>
    );
};

export default StockTally;
